import React, { useEffect, useRef, useState } from "react";
import Layout from "../../../components/Layout";
import { getEmployeeDetails } from "../../../api/EmployeeApi";
import {
  getAllRoutebyContractor,
  uploadBulkStreetData,
  getAllStreetbyRoute,
} from "../../../api/RouteApi";
import ToastFile from "../../../utils/ToastFile";
import { MdCheck, MdClose, MdControlPoint, MdDelete, MdModeEdit } from "react-icons/md";
import { Button, Form, Modal, Table } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";

const Street = () => {
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routes, setRoutes] = useState([]);
  const user = JSON.parse(localStorage.getItem("user"));
  const fetched = useRef(false);
  const navigate = useNavigate();
  const [streetAddress, setStreetAddress] = useState([]);
  const [isLoading, setIsloading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState("");

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm();

  const fetchStreetsByRoute = async (routeId) => {
    if (!routeId) {
      setStreetAddress([]);
      return;
    }

    setIsloading(true);
    try {
      const res = await getAllStreetbyRoute(
            routeId
          )
      if (res.status === 200) {
        setStreetAddress(res?.data?.data?.streets || []);
      } else {
        ToastFile("Failed to fetch streets", "error");
        setStreetAddress([]);
      }
    } catch (err) {
      console.error("Error fetching streets:", err);
      ToastFile("Error fetching streets", "error");
      setStreetAddress([]);
    } finally {
      setIsloading(false);
    }
  };

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    getRoute();

    const savedRoute = localStorage.getItem("selectedRoute");
    if (savedRoute) {
      const routeData = JSON.parse(savedRoute);
      setSelectedRoute(routeData);
      setValue("selectedRoute", routeData);
      fetchStreetsByRoute(routeData.value);
    }
  }, [setValue]);

  const getRoute = async () => {
    await Promise.all([
      getEmployeeDetails(user.id),
      getAllRoutebyContractor(user.tenant_id),
    ])
      .then(([employeeRes, routesRes]) => {
        if (employeeRes.status === 200 && routesRes.status === 200) {
          const employee = employeeRes?.data?.data;
          if (
            employee?.parcelSortRoutesList &&
            employee?.parcelSortRoutesList.length > 0
          ) {
            const options = routesRes?.data?.data?.routes
              .filter((route) =>
                employee.parcelSortRoutesList.includes(route._id),
              )
              .map((route) => ({
                value: route.route_id,
                label: route.routeName,
              }));
            setRoutes(options);
          } else {
            ToastFile(
              "You have no access to any routes. Please contact your contractor.",
              "info",
            );
          }
        } else {
          ToastFile("Failed to fetch employee details", "error");
        }
      })
      .catch((err) => {
        console.error("Error fetching employee details:", err);
        ToastFile("Error fetching employee details", "error");
      });
  };

  const handleDelete = async () => {
    const updatedStreets = streetAddress.filter((_, index) => index !== deleteIndex);
    setStreetAddress(updatedStreets);
    setDeleteModal(false);
    setDeleteIndex(null);
     handleUploadToServer(updatedStreets)
  };

  const handleUploadToServer = async (updatedStreets) => {
    let payload = {
      data: updatedStreets,
      route_id: selectedRoute?.value,
      tenant_id: user?.tenant_id,
      createdBy: user?.user_id,
      createdAt: new Date(),
      modifiedBy: user?.user_id,
      modifiedAt: new Date()
    };
    await uploadBulkStreetData(payload)
      .then((res) => {
        if (res.status === 200) {
          ToastFile("Uploaded successfully", "success");
        } else {
          ToastFile("Failed to upload", "error");
        }
      })
      .catch(() => {
        ToastFile("Failed to upload", "error");
      })
  };

  const addNewRow = () => {
    const newStreet = {
      _id: `temp_${Date.now()}`,
      streetName: "New Street",
      isNew: true
    };
    setStreetAddress([...streetAddress, newStreet]);
    setEditingIndex(streetAddress.length);
    setEditValue("New Street");
  };

  const handleEditStart = (index, currentValue) => {
    setEditingIndex(index);
    setEditValue(currentValue);
  };

  const handleEditSave = (index) => {
    if (editValue.trim() === "") {
      ToastFile("Street name cannot be empty", "error");
      return;
    }
    
    const updatedStreets = [...streetAddress];
    updatedStreets[index] = {
      ...updatedStreets[index],
      streetName: editValue.trim()
    };
    setStreetAddress(updatedStreets);
    setEditingIndex(null);
    setEditValue("");
    handleUploadToServer(updatedStreets)
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditValue("");
  };

  return (
    <Layout>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
              <h3 className="mb-2 mb-md-0">Street</h3>
            </div>

            <div className="row mb-3 d-flex justify-content-between">
              <div style={{ width: "400px" }}>
                <Form className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3">
                  <Form.Group className="flex-grow-1">
                    <Form.Label className="mb-2 fw-semibold">
                      Select Route
                    </Form.Label>
                    <Controller
                      control={control}
                      name="selectedRoute"
                      rules={{ required: false }}
                      render={({ field }) => (
                        <ReactSelect
                          {...field}
                          className=""
                          options={routes}
                          placeholder="Choose a route..."
                          onChange={(selected) => {
                            field.onChange(selected); // update form value
                            setSelectedRoute(selected);
                            // Save to localStorage
                            if (selected) {
                              localStorage.setItem(
                                "selectedRoute",
                                JSON.stringify(selected),
                              );
                              fetchStreetsByRoute(selected.value);
                            } else {
                              localStorage.removeItem("selectedRoute");
                              setStreetAddress([]);
                            }
                          }}
                        />
                      )}
                    />
                  </Form.Group>
                </Form>
              </div>
              <div style={{ width: "100px" }}>
                {selectedRoute && (
                  <Button
                    variant="outline-success"
                    onClick={() => handleUploadToServer()}
                    className="mt-2 mt-lg-0"
                  >
                    Save
                  </Button>
                )}
              </div>
            </div>

            {/* Desktop Table */}
            <div className="">
              <div className="table-responsive">
                <Table striped bordered hover className="table-sm">
                  <thead className="table-dark">
                    <tr style={{ textAlign: "center" }}>
                      <th className="text-wrap">Street Name</th>
                      <th className="text-nowrap">Action</th>
                    </tr>
                  </thead>
                  {isLoading ? (
                    <tbody>
                      <tr>
                        <td colSpan="4">
                          <Loader />
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                    <tbody>
                      {streetAddress.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center">
                            No Street Found
                          </td>
                        </tr>
                      ) : (
                        streetAddress.map((c, idx) => (
                          <tr key={idx}>
                            <td className="text-nowrap text-center">
                              {editingIndex === idx ? (
                                <div className="d-flex align-items-center gap-2">
                                  <input
                                    type="text"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    className="form-control form-control-sm"
                                    autoFocus
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleEditSave(idx);
                                      } else if (e.key === 'Escape') {
                                        handleEditCancel();
                                      }
                                    }}
                                  />
                                </div>
                              ) : (
                                <div>{c.streetName}</div>
                              )}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {editingIndex === idx ? (
                                <>
                                  <button
                                    className="btn btn-sm btn-outline-success border-0 me-1"
                                    onClick={() => handleEditSave(idx)}
                                  >
                                    <MdCheck size={18}/>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-secondary border-0"
                                    onClick={handleEditCancel}
                                  >
                                    <MdClose size={18}/>
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    className="btn btn-sm btn-outline-primary border-0"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => handleEditStart(idx, c.streetName)}
                                  >
                                    <MdModeEdit size={18} />
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger border-0"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      setDeleteIndex(idx);
                                      setDeleteModal(true);
                                    }}
                                  >
                                    <MdDelete size={18} />
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  )}
                </Table>
                <div className="d-flex justify-content-end">
                  <button
                    className="btn btn-sm btn-outline-success border-0"
                    style={{ cursor: "pointer" }}
                    onClick={() => addNewRow()}
                  >
                    <MdControlPoint size={30} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteModal} onHide={() => setDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this street?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default Street;
